import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import npsQuestionSetsFile from '../data/npsQuestionSets.json'
import { submitNpsResponse } from '../services/npsService'
import { NpsContext, NpsStatus } from '../hooks/NpsContext'
import { NpsQuestion, NpsQuestionSet, NpsQuestionSetsFile, NpsResponse, NpsResponseValue } from '../types/nps.types'
import NpsModal from './NpsModal'

interface Props {
    children?: React.ReactNode
}

const dataFile = npsQuestionSetsFile as NpsQuestionSetsFile

const validateQuestion = (question: NpsQuestion, value: NpsResponseValue | undefined, t: (key: string) => string): string | undefined => {
    if (!question.required) return undefined

    switch (question.type) {
        case 'rating':
            if (typeof value !== 'number') return t('Please select a rating')
            return undefined
        case 'multiple-choice':
            if (typeof value !== 'string' || value.length === 0) return t('Please select an option')
            return undefined
        case 'text':
            if (typeof value !== 'string' || value.trim().length === 0) return t('This field is required')
            return undefined
        default:
            return undefined
    }
}

const NpsProvider: React.FC<Props> = ({ children }) => {
    const { t } = useTranslation()
    const [activeSet, setActiveSet] = useState<NpsQuestionSet | null>(null)
    const [responses, setResponses] = useState<Record<string, NpsResponseValue | undefined>>({})
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<NpsStatus>('idle')
    const submitInFlight = useRef(false)

    const questionSets = useMemo<NpsQuestionSet[]>(() => dataFile.questionSets ?? [], [])

    const triggerNps = useCallback(
        (questionSetId: string) => {
            const nextSet = questionSets.find((set) => set.id === questionSetId)
            if (!nextSet) {
                console.warn(`NPS: question set "${questionSetId}" not found`)
                return
            }
            submitInFlight.current = false
            setActiveSet(nextSet)
            setResponses({})
            setErrors({})
            setStatus('open')
        },
        [questionSets],
    )

    const close = useCallback(() => {
        if (submitInFlight.current) return
        setActiveSet(null)
        setResponses({})
        setErrors({})
        setStatus('idle')
    }, [])

    const setResponse = useCallback((questionId: string, value: NpsResponseValue | undefined) => {
        setResponses((prev) => ({ ...prev, [questionId]: value }))
        setErrors((prev) => {
            if (!prev[questionId]) return prev
            const next = { ...prev }
            delete next[questionId]
            return next
        })
    }, [])

    const submit = useCallback(async () => {
        if (!activeSet) return
        if (submitInFlight.current) return
        if (status !== 'open') return

        const nextErrors: Record<string, string> = {}
        for (const question of activeSet.questions) {
            const error = validateQuestion(question, responses[question.id], t)
            if (error) nextErrors[question.id] = error
        }

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors)
            return
        }

        const payloadResponses: NpsResponse[] = activeSet.questions
            .map((question): NpsResponse | null => {
                const value = responses[question.id]
                if (value === undefined || value === null) return null
                if (typeof value === 'string' && value.length === 0) return null
                return { questionId: question.id, value }
            })
            .filter((entry): entry is NpsResponse => entry !== null)

        submitInFlight.current = true
        setStatus('submitting')
        setErrors({})

        try {
            await submitNpsResponse({ questionSetId: activeSet.id, responses: payloadResponses })
            setStatus('thankyou')
        } catch (error) {
            console.error('NPS submission failed', error)
            setStatus('open')
        } finally {
            submitInFlight.current = false
        }
    }, [activeSet, responses, status, t])

    const contextValue = useMemo(
        () => ({
            activeSet,
            responses,
            errors,
            status,
            triggerNps,
            setResponse,
            submit,
            close,
        }),
        [activeSet, responses, errors, status, triggerNps, setResponse, submit, close],
    )

    return (
        <NpsContext.Provider value={contextValue}>
            {children}
            <NpsModal />
        </NpsContext.Provider>
    )
}

export default NpsProvider
